import { useMemo } from "react";
import type { SiteConfig, GlobalTitleConfig, GlobalSubtitleConfig } from "@/types/config";
import type { TitleConfig } from "@/types/shared/title";
import { getFontStack } from "./fontUtils";
import { coerceBoolean } from "./booleanUtils";

export interface ResolvedTypography {
  fontFamily: string;
  fontStack: string;
  weights: string[];
  subsets: string[];
  tailwindWeightClass: string;
}

type SectionName =
  | "hero"
  | "about"
  | "experience"
  | "skills"
  | "projects"
  | "gallery"
  | "timeline"
  | "contact"
  | "social"
  | "testimonials"
  | "faq"
  | "services"
  | "features"
  | "pricing"
  | "team"
  | "cta";

type TypographyType = "title" | "subtitle" | "body" | "heading";

interface TypographyConfig {
  bodyFont?: string;
  bodyFontWeights?: string[];
  bodyFontSubsets?: string[];
}

interface SectionConfig {
  titleConfig?: TitleConfig;
  subtitleConfig?: TitleConfig;
}

/**
 * Get default weights for a given font type
 */
function getDefaultWeights(type: TypographyType): string[] {
  switch (type) {
    case "title":
    case "heading":
      return ["400", "700"];
    case "subtitle":
      return ["400", "600"];
    case "body":
      return ["400", "500"];
    default:
      return ["400", "700"];
  }
}

/**
 * Get default tailwind weight class for a given type
 */
function getDefaultTailwindWeight(type: TypographyType): string {
  switch (type) {
    case "title":
    case "heading":
      return "font-bold";
    case "subtitle":
      return "font-semibold";
    case "body":
      return "font-normal";
    default:
      return "font-normal";
  }
}

/**
 * Convert font weight number to tailwind class
 */
function weightToTailwindClass(weight: string): string {
  const weightMap: Record<string, string> = {
    "100": "font-thin",
    "200": "font-extralight",
    "300": "font-light",
    "400": "font-normal",
    "500": "font-medium",
    "600": "font-semibold",
    "700": "font-bold",
    "800": "font-extrabold",
    "900": "font-black",
  };
  return weightMap[weight] || "font-normal";
}

/**
 * Resolve typography configuration for a section
 * Merges: section config → global titleConfig/subtitleConfig → typography defaults
 */
export function resolveTypography(
  config: SiteConfig | undefined,
  _sectionName: SectionName,
  type: TypographyType,
  sectionConfig?: SectionConfig
): ResolvedTypography {
  if (!config) {
    // Fallback if no config
    const defaultWeights = getDefaultWeights(type);
    return {
      fontFamily: "",
      fontStack: getFontStack(undefined),
      weights: defaultWeights,
      subsets: ["latin"],
      tailwindWeightClass: getDefaultTailwindWeight(type),
    };
  }

  const typography = config.typography as TypographyConfig | undefined;
  const globalTitleConfig = config.titleConfig as GlobalTitleConfig | undefined;
  const globalSubtitleConfig = config.subtitleConfig as GlobalSubtitleConfig | undefined;

  let fontFamily: string | undefined;
  let fontWeights: string[] | undefined;
  let fontSubsets: string[] | undefined;
  let inheritBodyFont: boolean | undefined;

  // Priority 1: Section-specific config
  if (type === "title" && sectionConfig?.titleConfig) {
    const titleCfg = sectionConfig.titleConfig;
    if (titleCfg.fontFamily) {
      fontFamily = titleCfg.fontFamily;
      fontWeights = titleCfg.fontWeights;
      fontSubsets = titleCfg.fontSubsets;
    }
    if (titleCfg.inheritBodyFont !== undefined) {
      const normalized = coerceBoolean(titleCfg.inheritBodyFont);
      if (normalized !== undefined) {
        inheritBodyFont = normalized;
      }
    }
  } else if (type === "subtitle" && sectionConfig?.subtitleConfig) {
    const subtitleCfg = sectionConfig.subtitleConfig;
    if (subtitleCfg.fontFamily) {
      fontFamily = subtitleCfg.fontFamily;
      fontWeights = subtitleCfg.fontWeights;
      fontSubsets = subtitleCfg.fontSubsets;
    }
    if (subtitleCfg.inheritBodyFont !== undefined) {
      const normalized = coerceBoolean(subtitleCfg.inheritBodyFont);
      if (normalized !== undefined) {
        inheritBodyFont = normalized;
      }
    }
  }

  // Priority 2: Global title/subtitle config
  // Apply global config when preserveBlockClasses is explicitly false
  const applyGlobalTitleConfig = globalTitleConfig?.preserveBlockClasses === false;
  const applyGlobalSubtitleConfig = globalSubtitleConfig?.preserveBlockClasses === false;

  if (!fontFamily && type === "title" && applyGlobalTitleConfig) {
    if (globalTitleConfig.fontFamily) {
      fontFamily = globalTitleConfig.fontFamily;
      fontWeights = globalTitleConfig.fontWeights;
      fontSubsets = globalTitleConfig.fontSubsets;
    }
  } else if (!fontFamily && type === "subtitle" && applyGlobalSubtitleConfig) {
    if (globalSubtitleConfig.fontFamily) {
      fontFamily = globalSubtitleConfig.fontFamily;
      fontWeights = globalSubtitleConfig.fontWeights;
      fontSubsets = globalSubtitleConfig.fontSubsets;
    }
  }

  if (inheritBodyFont === undefined) {
    if (
      type === "title" &&
      applyGlobalTitleConfig &&
      globalTitleConfig.inheritBodyFont !== undefined
    ) {
      const normalized = coerceBoolean(globalTitleConfig.inheritBodyFont);
      if (normalized !== undefined) {
        inheritBodyFont = normalized;
      }
    } else if (
      type === "subtitle" &&
      applyGlobalSubtitleConfig &&
      globalSubtitleConfig.inheritBodyFont !== undefined
    ) {
      const normalized = coerceBoolean(globalSubtitleConfig.inheritBodyFont);
      if (normalized !== undefined) {
        inheritBodyFont = normalized;
      }
    }
  }

  // Apply inheritBodyFont flag
  const shouldInheritBodyFont = inheritBodyFont === true;
  if (shouldInheritBodyFont && typography?.bodyFont) {
    fontFamily = typography.bodyFont;
    fontWeights = typography.bodyFontWeights;
    fontSubsets = typography.bodyFontSubsets;
  }

  // Priority 3: Typography defaults
  if (!fontFamily) {
    fontFamily = typography?.bodyFont;
    fontWeights = typography?.bodyFontWeights;
    fontSubsets = typography?.bodyFontSubsets;
  }

  // Final fallback
  const resolvedFamily = fontFamily || "";
  const resolvedWeights = fontWeights && fontWeights.length > 0
    ? fontWeights
    : getDefaultWeights(type);
  const resolvedSubsets = fontSubsets && fontSubsets.length > 0
    ? fontSubsets
    : ["latin"];

  // Get the primary weight for tailwind class (usually the first or 400/700)
  const primaryWeight = resolvedWeights.includes("700")
    ? "700"
    : resolvedWeights.includes("400")
    ? "400"
    : resolvedWeights[0] || "400";

  return {
    fontFamily: resolvedFamily,
    fontStack: getFontStack(resolvedFamily),
    weights: resolvedWeights,
    subsets: resolvedSubsets,
    tailwindWeightClass: weightToTailwindClass(primaryWeight),
  };
}

/**
 * Hook to resolve typography for a section
 */
export function useResolvedTypography(
  config: SiteConfig | undefined,
  sectionName: SectionName,
  type: TypographyType,
  sectionConfig?: SectionConfig
): ResolvedTypography {
  return useMemo(
    () => resolveTypography(config, sectionName, type, sectionConfig),
    [config, sectionName, type, sectionConfig]
  );
}
